import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RequestHeader, RequestForm, RequestResult, RequestInfo } from '@/components/influencers'
import { validateInstagramUsername } from '@/utils/validation'
import { apiRequest, ApiError } from '@/utils/api'
import { useToast } from '@/hooks/use-toast'

export function RequestAnalyticsPage() {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [targetUsername, setTargetUsername] = useState('')
  const [days, setDays] = useState(7)
  const [validationError, setValidationError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [requestResult, setRequestResult] = useState<{success: boolean, message: string, requestId?: string} | null>(null)

  // Pre-fill username from URL params
  useEffect(() => {
    const usernameParam = searchParams.get('username')
    if (usernameParam) {
      setTargetUsername(usernameParam)
    }
  }, [searchParams])

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setValidationError('')
    setRequestResult(null)
    
    // Validate username
    const { isValid, error } = validateInstagramUsername(targetUsername)
    if (!isValid) {
      setValidationError(error)
      return
    }
    
    console.log('DEBUG: About to make request with days:', days, 'target:', targetUsername)
    setIsSubmitting(true)
    
    try {
      const url = `/v0_1/instagram/analytics/request?target=${encodeURIComponent(targetUsername)}&days=${days}`

      const result = await apiRequest(url, {
        method: 'POST'
      })
      
      setRequestResult({
        success: true,
        message: result.message,
        requestId: result.request_id
      })
      setTargetUsername('') // Clear form on success
      setDays(7) // Reset to default
    } catch (error: any) {
      const message = error?.response?.detail || error?.response?.message || error?.message || 'Failed to create analytics request'
      setRequestResult({
        success: false,
        message
      })
      if (error instanceof ApiError && error.status === 400 && message.includes('No available analytics remaining')) {
        toast({ title: 'Limit reached', description: 'No available analytics remaining.', variant: 'destructive' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <RequestHeader />
      
      <RequestForm
        targetUsername={targetUsername}
        setTargetUsername={setTargetUsername}
        days={days}
        setDays={setDays}
        validationError={validationError}
        isSubmitting={isSubmitting}
        onSubmit={handleCreateRequest}
      />
      
      {requestResult && (
        <RequestResult result={requestResult} />
      )}
      
      <RequestInfo />
    </div>
  )
} 